import { useState } from 'react';
import {
  mapGetAllRecipientGroupsResponseType,
  mapGetAllReportConfigurationsResponseType,
  ReportConfigurationType,
  useGetAllRecipientGroupsSuspense,
  useGetAllReportConfigurationsSuspense,
} from '@api';
import { ReportMailingForm, ReportMailingsTable } from '@common';
import { usePermissionContext } from '@hypeit-gmbh/hypeit-ui-core';
import { Divider, Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { Route } from 'src/routes/_rootLayout/venues/$id.reports';
import {
  getAddReportMailingFormSchema,
  getEditReportMailingFormSchema,
  getRecipientGroupOptions,
  mapVenueFormValuesToUpdateRequest,
  mapVenueReportMailingToFormValues,
} from './helpers';
import {
  useAddReportMailing,
  useDeleteReportMailing,
  useUpdateReportMailing,
} from './hooks';
import type {
  AddReportMailingFormValuesType,
  EditReportMailingFormValuesType,
} from './types';

export const VenueReportMailings: React.FC = () => {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const { hasPermission } = usePermissionContext();
  const [openedAdd, { open: openModalAdd, close: closeModalAdd }] =
    useDisclosure(false);
  const [selectedMailing, setSelectedMailing] =
    useState<ReportConfigurationType | null>(null);
  const { data: mailings } = useGetAllReportConfigurationsSuspense(
    { venueId: id },
    {
      query: {
        select: (response) =>
          mapGetAllReportConfigurationsResponseType(response).results,
      },
    }
  );
  const { data: recipientGroups } = useGetAllRecipientGroupsSuspense(
    { venueId: id },
    {
      query: {
        select: (response) =>
          mapGetAllRecipientGroupsResponseType(response).results,
      },
    }
  );
  const { addReportMailing } = useAddReportMailing();
  const { updateReportMailing } = useUpdateReportMailing();
  const { handleDeleteReportMailing } = useDeleteReportMailing();

  const canWrite = hasPermission({ permission: 'REPORTS', action: 'WRITE' });

  const closeModalEdit = () => setSelectedMailing(null);

  const handleAddReportMailing = async ({
    label,
  }: AddReportMailingFormValuesType) => {
    if (
      await addReportMailing({
        label,
        configurationOptions: { kind: 'VENUE', venueId: id },
      })
    ) {
      closeModalAdd();
    }
  };

  const handleUpdateReportMailing = async (
    values: EditReportMailingFormValuesType
  ) => {
    if (!selectedMailing) return;

    if (
      await updateReportMailing(
        selectedMailing.id,
        mapVenueFormValuesToUpdateRequest(values)
      )
    ) {
      closeModalEdit();
    }
  };

  return (
    <>
      <ReportMailingsTable
        variant="VENUE"
        mailings={mailings}
        canAdd={canWrite}
        onAdd={openModalAdd}
        canEdit={canWrite}
        onEdit={setSelectedMailing}
        onDelete={handleDeleteReportMailing}
      />
      <Modal
        opened={openedAdd}
        onClose={closeModalAdd}
        title={t('REPORTS.MAILING_FORM.ADD_TITLE')}
      >
        <ReportMailingForm.Root<AddReportMailingFormValuesType>
          schema={getAddReportMailingFormSchema(t)}
          defaultValues={{ label: '' }}
          onSave={handleAddReportMailing}
          onCancel={closeModalAdd}
        >
          <ReportMailingForm.Label<AddReportMailingFormValuesType> fieldName="label" />
        </ReportMailingForm.Root>
      </Modal>
      <Modal
        opened={selectedMailing !== null}
        onClose={closeModalEdit}
        title={t('REPORTS.MAILING_FORM.EDIT_TITLE')}
        size="lg"
      >
        {selectedMailing && (
          <ReportMailingForm.Root<EditReportMailingFormValuesType>
            schema={getEditReportMailingFormSchema(t)}
            defaultValues={mapVenueReportMailingToFormValues(selectedMailing)}
            onSave={handleUpdateReportMailing}
            onCancel={closeModalEdit}
          >
            <ReportMailingForm.SettingsTitle />
            <ReportMailingForm.Label<EditReportMailingFormValuesType> fieldName="label" />
            <ReportMailingForm.Active<EditReportMailingFormValuesType> fieldName="isEnabled" />
            <ReportMailingForm.RecipientGroup<EditReportMailingFormValuesType>
              fieldName="recipientGroupId"
              data={getRecipientGroupOptions(recipientGroups, t)}
            />
            <ReportMailingForm.SendDay<EditReportMailingFormValuesType> fieldName="sendWeekday" />
            <ReportMailingForm.SendTime<EditReportMailingFormValuesType>
              hourFieldName="sendHour"
              minuteFieldName="sendMinute"
            />
            <ReportMailingForm.Language<EditReportMailingFormValuesType> fieldName="locale" />
            <Divider />
            <ReportMailingForm.EmailNote<EditReportMailingFormValuesType> fieldName="profileNote" />
            <Divider />
            <ReportMailingForm.InternalNote<EditReportMailingFormValuesType> fieldName="internalNote" />
          </ReportMailingForm.Root>
        )}
      </Modal>
    </>
  );
};
